import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, Phone, Clock, ArrowRight, AlertCircle, UtensilsCrossed, Star, PawPrint, Wheat, X, ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import api, { API } from "../lib/api";
import useDescTranslate from "../lib/useDescTranslate";

const FALLBACK_IMGS = [
    "https://images.unsplash.com/photo-1766832255363-c9f060ade8b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwZGFyayUyMHJlc3RhdXJhbnQlMjBpbnRlcmlvciUyMGV2ZW5pbmclMjBsdXh1cnl8ZW58MHx8fHwxNzc2OTY1MTgzfDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1759931002745-50708843e356?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwaXRhbGlhbiUyMGZvb2QlMjBkYXJrJTIwYmFja2dyb3VuZCUyMGx1eHVyeXxlbnwwfHx8fDE3NzY5NjUxODN8MA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1771360873707-f435ef9732ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxnb3VybWV0JTIwaXRhbGlhbiUyMGZvb2QlMjBkYXJrJTIwYmFja2dyb3VuZCUyMGx1eHVyeXxlbnwwfHx8fDE3NzY5NjUxODN8MA&ixlib=rb-4.1.0&q=85",
];

export default function RestaurantHome() {
    const { slug } = useParams();
    const { t } = useTranslation();
    const [restaurant, setRestaurant] = useState(null);
    const [menu, setMenu] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/r/${slug}`).then((r) => setRestaurant(r.data)).catch(() => setError(true));
        api.get(`/r/${slug}/menu`).then((r) => setMenu(r.data)).catch(() => {});
        api.get(`/r/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    }, [slug]);

    // Translate the restaurant's description dynamically when language ≠ Italian.
    const { tx: txDesc } = useDescTranslate(restaurant?.description ? [restaurant.description] : []);

    // Scroll to #reviews anchor after data has loaded (React Router ignores hash by default)
    useEffect(() => {
        if (reviews.length > 0 && window.location.hash === "#reviews") {
            const el = document.getElementById("reviews");
            if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        }
    }, [reviews]);

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-ink px-6 pt-32">
                <div className="text-center max-w-md">
                    <AlertCircle size={48} className="text-gold mx-auto mb-4" strokeWidth={1} />
                    <h1 className="font-serif text-3xl text-white">{t("restaurant_home.welcome")}</h1>
                    <Link to="/" className="inline-block mt-6 text-gold tracking-widest text-xs uppercase hover:text-gold-light" data-testid="back-home-link">← {t("common.home")}</Link>
                </div>
            </div>
        );
    }
    if (!restaurant) return <div className="min-h-screen flex items-center justify-center bg-ink"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

    const gallery = restaurant.gallery || [];
    const heroImg = gallery[0] ? `${API}/files/${gallery[0]}` : FALLBACK_IMGS[0];
    const secondaryImgs = [1, 2, 3].map((i) => gallery[i] ? `${API}/files/${gallery[i]}` : null).filter(Boolean);

    return (
        <div className="min-h-screen bg-ink" data-testid="restaurant-home">
            {/* HERO with first gallery image */}
            <section className="relative min-h-screen flex items-center overflow-hidden grain">
                <div className="absolute inset-0 z-0">
                    <img src={heroImg} alt={restaurant.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/60 to-black/95" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20 w-full">
                    <div className="max-w-3xl animate-fade-up">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="h-px w-12 bg-gold" />
                            <span className="text-gold text-xs tracking-[0.4em] uppercase">{t("restaurant_home.welcome")}</span>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light text-white leading-[1.05] tracking-tight" data-testid="restaurant-name">
                            {restaurant.name}
                        </h1>
                        {restaurant.gluten_free && (
                            <div className="mt-4 inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 px-3 py-1.5 rounded-sm text-xs font-semibold tracking-widest uppercase" data-testid="gluten-free-badge">
                                <Wheat size={13} strokeWidth={1.8} /> {t("badges.gluten_free")}
                            </div>
                        )}
                        {restaurant.description && (
                            <p className="mt-8 text-lg text-gray-300 max-w-xl font-light leading-relaxed" data-testid="restaurant-description">
                                {txDesc(restaurant.description)}
                            </p>
                        )}
                        {restaurant.review_count > 0 && (
                            <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="hero-rating">
                                <div className="flex gap-0.5">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star key={n} size={16}
                                            className={n <= Math.round(restaurant.avg_rating) ? "text-gold fill-gold" : "text-white/20"}
                                            strokeWidth={1.5} />
                                    ))}
                                </div>
                                <span className="text-white text-sm">
                                    <strong>{restaurant.avg_rating.toFixed(1)}</strong>
                                    <span className="text-gray-400 ml-2">({t("restaurant_home.reviews_count", { count: restaurant.review_count })})</span>
                                </span>
                                <a href="#reviews" className="text-[10px] uppercase tracking-widest text-gold hover:text-gold-light underline underline-offset-2" data-testid="hero-read-reviews-link">
                                    {t("restaurant_home.read_reviews")}
                                </a>
                            </div>
                        )}
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Link to={`/r/${slug}/book`} data-testid="hero-book-btn"
                                className="group inline-flex items-center gap-3 bg-gold text-black font-semibold px-8 py-4 rounded-sm hover:bg-gold-light transition-all tracking-wide">
                                <Calendar size={18} strokeWidth={2} />
                                {t("common.book_table")}
                                <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                            </Link>
                            <a href="#gallery" className="inline-flex items-center gap-3 border border-white/20 text-white px-8 py-4 rounded-sm hover:border-gold hover:text-gold transition-all tracking-wide">
                                {t("common.discover")}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* GALLERY */}
            {secondaryImgs.length > 0 && (
                <section id="gallery" className="py-24 px-6 md:px-10">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex items-center gap-3 mb-10">
                            <div className="h-px w-10 bg-gold" />
                            <span className="text-gold text-xs tracking-[0.4em] uppercase">Atmosfera & Cucina</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="restaurant-gallery">
                            {secondaryImgs.map((src, i) => (
                                <div key={src} className="relative aspect-[4/5] overflow-hidden rounded-sm group" data-testid={`gallery-img-${i}`}>
                                    <img src={src} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* MENU PREVIEW */}
            {menu.length > 0 && (
                <MenuExplorer menu={menu} bookHref={`/r/${slug}/book`} />
            )}

            {/* REVIEWS */}
            {reviews.length > 0 && (
                <section id="reviews" className="py-24 px-6 md:px-10 border-t border-white/10" data-testid="reviews-section">
                    <div className="max-w-5xl mx-auto">
                        <div className="flex items-center gap-3 mb-10">
                            <div className="h-px w-10 bg-gold" />
                            <span className="text-gold text-xs tracking-[0.4em] uppercase">Dicono di noi</span>
                        </div>
                        <h2 className="font-serif text-4xl lg:text-5xl text-white mb-12">Recensioni <span className="italic text-gold">dei clienti</span></h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            {reviews.slice(0, 8).map((rev) => (
                                <div key={rev.review_id} className="bg-ink-surface border border-white/10 rounded-sm p-6 hover:border-gold/30 transition-colors" data-testid={`review-${rev.review_id}`}>
                                    <div className="flex items-center gap-2 mb-3">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star key={n} size={14}
                                                className={n <= rev.stars ? "text-gold fill-gold" : "text-white/15"}
                                                strokeWidth={1.5} />
                                        ))}
                                    </div>
                                    {rev.text && <p className="text-sm text-gray-300 leading-relaxed italic">"{rev.text}"</p>}
                                    <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-gray-500">
                                        — {rev.user_name || "Cliente"}
                                    </div>
                                    {rev.owner_reply && (
                                        <div className="mt-4 pl-4 border-l-2 border-gold/60 bg-gold/5 rounded-r-sm p-4" data-testid={`owner-reply-${rev.review_id}`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Risposta del ristorante</span>
                                            </div>
                                            <p className="text-sm text-gray-200 leading-relaxed">{rev.owner_reply}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* INFO */}
            <section id="info" className="py-20 px-6 md:px-10 border-t border-white/10">
                <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-8">
                    {restaurant.address && <InfoItem icon={MapPin} label={t("restaurant_home.address_label")} text={restaurant.address} />}
                    {restaurant.phone && <InfoItem icon={Phone} label={t("restaurant_home.phone")} text={restaurant.phone} />}
                    {restaurant.opening_hours && <InfoItem icon={Clock} label={t("restaurant_home.opening_hours")} text={restaurant.opening_hours} />}
                    <InfoItem
                        icon={PawPrint}
                        label={restaurant.pets_allowed ? t("restaurant_home.pets_accepted") : t("restaurant_home.pets_not_accepted")}
                        text=""
                        accent={restaurant.pets_allowed ? "emerald" : "rose"}
                        testid="pets-allowed-badge"
                    />
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-32 px-6 md:px-10 border-t border-white/10">
                <div className="max-w-5xl mx-auto text-center">
                    <h2 className="font-serif text-5xl lg:text-6xl text-white leading-tight">
                        {t("restaurant_home.table_waiting_1")}<br /><span className="italic gold-shimmer">{t("restaurant_home.table_waiting_2")}.</span>
                    </h2>
                    <p className="mt-5 text-sm text-gray-400 max-w-xl mx-auto">{t("restaurant_home.table_waiting_subtitle")}</p>
                    <Link to={`/r/${slug}/book`} data-testid="cta-book-btn"
                        className="inline-flex items-center gap-3 mt-10 bg-gold text-black font-semibold px-10 py-5 rounded-sm hover:bg-gold-light tracking-wide">
                        <Calendar size={20} /> {t("restaurant_home.book_now")}
                    </Link>
                </div>
            </section>

            <footer className="py-8 px-6 text-center border-t border-white/10">
                <p className="text-xs text-gray-500 tracking-widest uppercase">{restaurant.name} · Powered by Prenova by Renova360</p>
            </footer>
        </div>
    );
}

function InfoItem({ icon: Icon, label, text, accent, testid }) {
    const accentMap = {
        emerald: { icon: "text-emerald-300", text: "text-emerald-300" },
        rose: { icon: "text-rose-400", text: "text-rose-400" },
    };
    const tone = accentMap[accent] || { icon: "text-gold", text: "text-white" };
    return (
        <div className="flex items-start gap-4" data-testid={testid}>
            <Icon size={20} className={`${tone.icon} mt-1`} strokeWidth={1.5} />
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gray-500">{label}</div>
                <div className={`${tone.text} mt-1`}>{text}</div>
            </div>
        </div>
    );
}


// Standard ordering for Italian menu categories
const CATEGORY_ORDER = ["Antipasti", "Primi", "Secondi di carne", "Secondi di pesce", "Secondi", "Pizze", "Contorni", "Dolci", "Dessert", "Bevande"];
const ITEMS_PER_PAGE = 9;

export function MenuExplorer({ menu, bookHref }) {
    const { t } = useTranslation();

    // Translate dish DESCRIPTIONS (not names) when language ≠ italian.
    // Backend caches translations in MongoDB so subsequent loads are free.
    const descriptions = useMemo(
        () => (menu || []).map((m) => m.description).filter(Boolean),
        [menu],
    );
    const { tx: tDesc } = useDescTranslate(descriptions);

    const grouped = useMemo(() => {
        const out = {};
        for (const m of menu) {
            const c = m.category || "Altro";
            (out[c] ||= []).push(m);
        }
        return out;
    }, [menu]);

    const categories = useMemo(() => {
        const present = Object.keys(grouped);
        const ordered = CATEGORY_ORDER.filter((c) => present.includes(c));
        const extras = present.filter((c) => !CATEGORY_ORDER.includes(c)).sort();
        return [...ordered, ...extras];
    }, [grouped]);

    const [activeCat, setActiveCat] = useState("");
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (categories.length > 0 && !activeCat) setActiveCat(categories[0]);
    }, [categories, activeCat]);

    useEffect(() => { setPage(1); }, [activeCat]);

    const items = activeCat ? (grouped[activeCat] || []) : [];
    const totalPages = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE));
    const currentPage = Math.min(page, totalPages);
    const paginated = items.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    if (categories.length === 0) return null;

    return (
        <section className="py-24 px-6 md:px-10 border-t border-white/10" data-testid="menu-explorer">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-px w-10 bg-gold" />
                            <span className="text-gold text-xs tracking-[0.4em] uppercase">{t("restaurant_home.our_cuisine")}</span>
                        </div>
                        <h2 className="font-serif text-4xl lg:text-5xl text-white">{t("restaurant_home.menu_full_word").split(" ")[0]} <span className="italic text-gold">{t("restaurant_home.menu")}</span></h2>
                    </div>
                    <Link to={bookHref} className="inline-flex items-center gap-2 text-gold text-sm tracking-widest uppercase hover:text-gold-light" data-testid="book-from-menu-btn">
                        {t("common.book")} <ArrowRight size={14} />
                    </Link>
                </div>

                {/* Category tabs */}
                <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-5" data-testid="menu-categories">
                    {categories.map((c) => {
                        const active = c === activeCat;
                        const count = (grouped[c] || []).length;
                        return (
                            <button
                                key={c}
                                onClick={() => setActiveCat(c)}
                                data-testid={`menu-cat-${c.replace(/\s+/g, "-")}`}
                                className={`text-[11px] tracking-widest uppercase px-3 py-2 rounded-sm border transition-all ${
                                    active
                                        ? "bg-gold text-black border-gold"
                                        : "border-white/15 text-gray-300 hover:border-gold/40 hover:text-gold"
                                }`}
                            >
                                {t(`menu_categories.${c}`, { defaultValue: c })} <span className={active ? "text-black/60" : "text-gray-500"}>({count})</span>
                            </button>
                        );
                    })}
                </div>

                {/* Items grid (3 cols × 3 rows = 9 per page) */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="menu-items-grid">
                    {paginated.map((m) => (
                        <button
                            key={m.menu_item_id}
                            type="button"
                            onClick={() => setSelected(m)}
                            data-testid={`menu-item-${m.menu_item_id}`}
                            className="group bg-ink-surface border border-white/10 rounded-sm overflow-hidden hover:border-gold/60 transition-all text-left flex hover:-translate-y-0.5"
                        >
                            {m.photo_path ? (
                                <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 bg-ink overflow-hidden">
                                    <img src={`${API}/files/${m.photo_path}`} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                </div>
                            ) : (
                                <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 bg-gold/5 border-r border-white/5 flex items-center justify-center">
                                    <UtensilsCrossed size={22} className="text-gold/50" strokeWidth={1.2} />
                                </div>
                            )}
                            <div className="flex-1 p-4 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-serif text-lg text-white group-hover:text-gold transition-colors line-clamp-1">{m.name}</h3>
                                    <div className="text-gold font-semibold whitespace-nowrap text-sm">€ {(m.price_cents / 100).toFixed(2)}</div>
                                </div>
                                {m.description && <p className="text-[11px] text-gray-400 mt-1.5 line-clamp-2 leading-relaxed">{tDesc(m.description)}</p>}
                                <div className="text-[9px] uppercase tracking-widest text-gold/60 mt-2 inline-flex items-center gap-1">
                                    {t("common.details")} <ArrowRight size={9} />
                                </div>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-10 flex items-center justify-center gap-2 text-xs flex-wrap" data-testid="menu-pagination">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            data-testid="menu-prev-btn"
                            className="px-3 py-2 border border-white/15 rounded-sm text-gray-300 hover:border-gold/40 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        {Array.from({ length: totalPages }).map((_, i) => {
                            const n = i + 1;
                            const active = n === currentPage;
                            return (
                                <button
                                    key={n}
                                    onClick={() => setPage(n)}
                                    data-testid={`menu-page-${n}`}
                                    className={`min-w-[34px] py-2 rounded-sm border tracking-widest ${
                                        active ? "bg-gold text-black border-gold" : "border-white/15 text-gray-300 hover:border-gold/40"
                                    }`}
                                >
                                    {n}
                                </button>
                            );
                        })}
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            data-testid="menu-next-btn"
                            className="px-3 py-2 border border-white/15 rounded-sm text-gray-300 hover:border-gold/40 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ChevronRight size={14} />
                        </button>
                        <span className="ml-3 text-gray-500" data-testid="menu-pagination-info">
                            {t("common.page")} {currentPage} {t("common.of")} {totalPages} · {items.length} {t("common.dishes")}
                        </span>
                    </div>
                )}

                {selected && <MenuItemDialog item={selected} translatedDescription={selected.description ? tDesc(selected.description) : null} onClose={() => setSelected(null)} bookHref={bookHref} />}
            </div>
        </section>
    );
}

function MenuItemDialog({ item, translatedDescription, onClose, bookHref }) {
    const { t } = useTranslation();
    const description = translatedDescription || item.description;
    return (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose} data-testid="menu-item-dialog">
            <div className="bg-ink-surface border border-white/10 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {item.photo_path ? (
                    <div className="aspect-[16/9] overflow-hidden bg-ink">
                        <img src={`${API}/files/${item.photo_path}`} alt={item.name} className="w-full h-full object-cover" data-testid="menu-item-dialog-photo" />
                    </div>
                ) : (
                    <div className="aspect-[16/6] bg-gradient-to-br from-gold/10 to-transparent border-b border-white/5 flex items-center justify-center">
                        <UtensilsCrossed size={42} className="text-gold/50" strokeWidth={1} />
                    </div>
                )}
                <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">{t(`menu_categories.${item.category}`, { defaultValue: item.category })}</div>
                            <h3 className="font-serif text-3xl text-white" data-testid="menu-item-dialog-name">{item.name}</h3>
                        </div>
                        <button onClick={onClose} data-testid="menu-item-dialog-close" className="text-gray-400 hover:text-white shrink-0"><X size={20} /></button>
                    </div>
                    <div className="mt-3 text-2xl text-gold font-semibold" data-testid="menu-item-dialog-price">
                        € {(item.price_cents / 100).toFixed(2)}
                    </div>
                    {item.description && (
                        <p className="mt-4 text-sm text-gray-300 leading-relaxed whitespace-pre-line" data-testid="menu-item-dialog-description">
                            {description}
                        </p>
                    )}
                    {item.available === false && (
                        <div className="mt-4 inline-flex items-center gap-2 bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-sm">
                            {t("restaurant_home.not_available")}
                        </div>
                    )}
                    <div className="mt-7 pt-5 border-t border-white/10 flex items-center justify-end gap-3">
                        <button onClick={onClose} className="text-[11px] tracking-widest uppercase text-gray-400 hover:text-white px-3 py-2">
                            {t("common.close")}
                        </button>
                        <Link
                            to={bookHref}
                            data-testid="menu-item-dialog-book-btn"
                            className="bg-gold text-black font-semibold px-5 py-2.5 rounded-sm hover:bg-gold-light tracking-widest uppercase text-[11px] inline-flex items-center gap-2"
                        >
                            {t("common.book_table")} <ArrowRight size={12} />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
